import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, hasSupabaseConfig } from '../lib/supabase'
import { clearLocalSessionData, readProfile, saveProfile } from '../lib/cache'
import type { Profile } from '../types'
import {
  SESSION_ACTIVITY_PERSIST_INTERVAL_MS,
  SESSION_ACTIVITY_STORAGE_KEY,
  SESSION_INACTIVITY_EXPIRED_KEY,
  SESSION_TIMEOUT_NOTICE,
  SESSION_TIMEOUT_NOTICE_KEY,
  hasSessionTimedOut,
  inactivityRemainingMs,
  parseActivityTimestamp,
} from './sessionTimeout'

type AuthContextValue = {
  session: Session | null
  profile: Profile | null
  loading: boolean
  offlineProfile: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function getProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id,email,nome,role,ativo')
    .eq('id', userId)
    .single()

  if (error) throw error
  return data as Profile
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [offlineProfile, setOfflineProfile] = useState(false)

  async function resolveProfile(currentSession: Session | null) {
    if (!currentSession?.user) {
      setProfile(null)
      setOfflineProfile(false)
      return
    }

    try {
      const fresh = await getProfile(currentSession.user.id)
      saveProfile(fresh)
      setProfile(fresh)
      setOfflineProfile(false)
    } catch (error) {
      const cached = readProfile()
      if (cached?.id === currentSession.user.id) {
        setProfile(cached)
        setOfflineProfile(true)
        return
      }
      throw error
    }
  }

  useEffect(() => {
    if (!hasSupabaseConfig) {
      setLoading(false)
      return
    }

    let active = true

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return

      const inactivityExpired = Boolean(localStorage.getItem(SESSION_INACTIVITY_EXPIRED_KEY))
      if (data.session && inactivityExpired) {
        clearLocalSessionData()
        setSession(null)
        setProfile(null)
        setOfflineProfile(false)
        await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)
        if (active) setLoading(false)
        return
      }

      setSession(data.session)
      try {
        await resolveProfile(data.session)
      } catch {
        setProfile(null)
      } finally {
        if (active) setLoading(false)
      }
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (nextSession && localStorage.getItem(SESSION_INACTIVITY_EXPIRED_KEY)) {
        setSession(null)
        setProfile(null)
        setOfflineProfile(false)
        return
      }

      setSession(nextSession)
      window.setTimeout(() => {
        resolveProfile(nextSession).catch(() => setProfile(null))
      }, 0)
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!session?.user) return

    let timeoutId: number | undefined
    let signingOut = false
    let lastActivityAt = parseActivityTimestamp(localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY)) ?? Date.now()
    let lastPersistedAt = parseActivityTimestamp(localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY)) ?? 0
    let lastScheduledAt = 0

    function persistActivity(force = false) {
      if (!force && lastActivityAt - lastPersistedAt < SESSION_ACTIVITY_PERSIST_INTERVAL_MS) return
      localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(lastActivityAt))
      lastPersistedAt = lastActivityAt
    }

    function clearTimer() {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      timeoutId = undefined
    }

    async function expireSession() {
      if (signingOut) return

      const storedActivity = parseActivityTimestamp(localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY))
      if (storedActivity && storedActivity > lastActivityAt) lastActivityAt = storedActivity
      if (!hasSessionTimedOut(lastActivityAt)) {
        scheduleTimeout()
        return
      }

      signingOut = true
      clearTimer()
      localStorage.setItem(SESSION_INACTIVITY_EXPIRED_KEY, String(Date.now()))
      localStorage.setItem(SESSION_TIMEOUT_NOTICE_KEY, SESSION_TIMEOUT_NOTICE)
      localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY)
      clearLocalSessionData()
      setProfile(null)
      setOfflineProfile(false)
      setSession(null)

      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)
    }

    function scheduleTimeout() {
      clearTimer()
      const storedActivity = parseActivityTimestamp(localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY))
      if (storedActivity && storedActivity > lastActivityAt) lastActivityAt = storedActivity

      if (hasSessionTimedOut(lastActivityAt)) {
        void expireSession()
        return
      }

      timeoutId = window.setTimeout(() => void expireSession(), Math.max(1000, inactivityRemainingMs(lastActivityAt)))
    }

    function registerActivity() {
      const now = Date.now()
      lastActivityAt = now
      persistActivity()

      if (now - lastScheduledAt >= SESSION_ACTIVITY_PERSIST_INTERVAL_MS) {
        lastScheduledAt = now
        scheduleTimeout()
      }
    }

    function syncStoredActivity(event: StorageEvent) {
      if (event.key !== SESSION_ACTIVITY_STORAGE_KEY || !event.newValue) return
      const syncedAt = parseActivityTimestamp(event.newValue)
      if (!syncedAt || syncedAt <= lastActivityAt) return
      lastActivityAt = syncedAt
      lastPersistedAt = Math.max(lastPersistedAt, syncedAt)
      scheduleTimeout()
    }

    function checkOnReturn() {
      if (document.visibilityState === 'visible') scheduleTimeout()
    }

    function persistBeforeLeaving() {
      persistActivity(true)
    }

    if (!localStorage.getItem(SESSION_ACTIVITY_STORAGE_KEY)) persistActivity(true)
    scheduleTimeout()

    const activityEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    for (const eventName of activityEvents) window.addEventListener(eventName, registerActivity, { passive: true })
    window.addEventListener('storage', syncStoredActivity)
    window.addEventListener('focus', scheduleTimeout)
    window.addEventListener('pagehide', persistBeforeLeaving)
    document.addEventListener('visibilitychange', checkOnReturn)

    return () => {
      clearTimer()
      for (const eventName of activityEvents) window.removeEventListener(eventName, registerActivity)
      window.removeEventListener('storage', syncStoredActivity)
      window.removeEventListener('focus', scheduleTimeout)
      window.removeEventListener('pagehide', persistBeforeLeaving)
      document.removeEventListener('visibilitychange', checkOnReturn)
    }
  }, [session?.user?.id])

  const value = useMemo<AuthContextValue>(() => ({
    session,
    profile,
    loading,
    offlineProfile,
    signIn: async (email, password) => {
      const previousExpiredAt = localStorage.getItem(SESSION_INACTIVITY_EXPIRED_KEY)
      const attemptAt = Date.now()
      localStorage.removeItem(SESSION_INACTIVITY_EXPIRED_KEY)
      localStorage.setItem(SESSION_ACTIVITY_STORAGE_KEY, String(attemptAt))

      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY)
        if (previousExpiredAt) localStorage.setItem(SESSION_INACTIVITY_EXPIRED_KEY, previousExpiredAt)
        throw error
      }

      localStorage.removeItem(SESSION_TIMEOUT_NOTICE_KEY)
    },
    signOut: async () => {
      localStorage.removeItem(SESSION_ACTIVITY_STORAGE_KEY)
      localStorage.removeItem(SESSION_INACTIVITY_EXPIRED_KEY)
      localStorage.removeItem(SESSION_TIMEOUT_NOTICE_KEY)
      clearLocalSessionData()
      await supabase.auth.signOut()
      setProfile(null)
      setSession(null)
    },
    refreshProfile: async () => {
      if (session) await resolveProfile(session)
    },
  }), [session, profile, loading, offlineProfile])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return context
}
